// PanelViewSettings — what a later interview panel sees of a candidate's
// EARLIER rounds (Nilay Sharma's proposal pr_f443826425). The group decided
// (Aarti Pathak, 23 Sep 2026) that later panels see the earlier rounds'
// marks and remarks; "remarks first" is the recorded fallback if the group
// reverses it: remarks always, earlier marks only once the panellist has
// submitted their own. Within a round nothing changes either way. Read by HR;
// saved by a recruitment administrator (GET / PUT
// /api/school-settings/panel-view), with who and when kept.
// @story HIR-TC2

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Select } from '../../ds';
import { Loading, Note } from '../../components/page';

const OPTIONS = [
  { value: 'visible', label: "Marks and remarks (the group's decision)" },
  { value: 'after_own', label: "Remarks first: earlier marks after the panellist's own" },
];

export default function PanelViewSettings() {
  const [st, setSt] = useState({ phase: 'loading' });
  const [mode, setMode] = useState('visible');
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const canSave = user.role === 'admin';

  useEffect(() => {
    api.get('/school-settings/panel-view')
      .then((r) => { setSt({ phase: 'ready', ...r.data.data }); setMode(r.data.data.earlierMarks || 'visible'); })
      .catch(() => setSt({ phase: 'error' }));
  }, []);

  const save = () => {
    setSaving(true);
    setMsg(null);
    api.put('/school-settings/panel-view', { earlierMarks: mode })
      .then((r) => { setSt({ phase: 'ready', ...r.data.data }); setMsg({ tone: 'success', text: 'Saved. Panels see it from the next page they open.' }); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not save. Try again.' }))
      .finally(() => setSaving(false));
  };

  if (st.phase === 'loading') return <Loading label="Reading the panel setting" />;
  if (st.phase === 'error') return <Note tone="warning">The panel setting could not be read just now. Reload the page to try again.</Note>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        What an interview panel sees of the candidate's earlier rounds while marking a later one. Within a round,
        panellists always see each other's marks only after submitting their own.
      </p>
      <Select label="Earlier rounds" value={mode} disabled={!canSave}
        options={OPTIONS}
        hint={st.changedAt ? `Last changed ${new Date(st.changedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}${st.changedBy ? ` by ${st.changedBy}` : ''}` : undefined}
        onChange={(v) => setMode(v)} />
      {canSave && <div><Button onClick={save} disabled={saving || mode === st.earlierMarks}>Save</Button></div>}
      {!canSave && <Note tone="info">Only a recruitment administrator can change this.</Note>}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}
